import React, { useState, useEffect, useRef, useCallback } from 'react';
import { filterInput } from '../../utils/inputSanitization';
import { motion } from 'motion/react';
import { debounce } from 'lodash';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Chip,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { ReadOnlyButton, ReadOnlyIconButton } from '../common/ReadOnlyButton';
import PolicyGeneratorLink from '../common/PolicyGeneratorLink';

const containerVariants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

// Helper function to count non-empty inline policies
const countInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return 0;
  return Object.values(inlinePolicies).filter(policy => policy && policy.trim() !== '').length;
};

// Helper function to filter out empty inline policies
const filterNonEmptyInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return {};
  const filtered = {};
  Object.entries(inlinePolicies).forEach(([name, content]) => {
    if (content && content.trim() !== '') {
      filtered[name] = content;
    }
  });
  return filtered;
};

// Format ISO 8601 duration to a more readable format
const formatDuration = (duration) => {
  if (!duration) return 'N/A';

  if (duration.startsWith('PT')) {
    const hours = duration.match(/(\d+)H/);
    const minutes = duration.match(/(\d+)M/);

    let result = '';
    if (hours) result += `${hours[1]} hour${hours[1] !== '1' ? 's' : ''} `;
    if (minutes) result += `${minutes[1]} minute${minutes[1] !== '1' ? 's' : ''}`;

    return result.trim() || 'N/A';
  }

  return duration;
};

const PermissionSetDetails = ({
  permissionSet,
  awsManagedPolicies,
  onClose,
  onUpdate,
  onDelete,
  fetchAwsPolicies
}) => {
  const confirm = useConfirm();
  const isReadOnly = useReadOnlyMode();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    sessionDuration: '',
    relayState: '',
    managedPolicies: [],
    inlinePolicies: {},
    customerManagedPolicyIds: []
  });

  const [cmpLibrary, setCmpLibrary] = useState([]);

  const [policySearchTerm, setPolicySearchTerm] = useState('');
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState('');
  const [policySortBy, setPolicySortBy] = useState('name');
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [policiesPerPage] = useState(10);

  const [inlinePolicyName, setInlinePolicyName] = useState('');
  const [inlinePolicyContent, setInlinePolicyContent] = useState('');
  const [editingPolicyName, setEditingPolicyName] = useState(null); // Track which policy is being edited

  const [policyValidation, setPolicyValidation] = useState({
    isValidating: false,
    result: null,
    error: null
  });

  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    isDeleting: false,
    error: null,
  });

  const policiesFetched = useRef(false);

  useEffect(() => {
    if (permissionSet) {
      const managedPolicies = permissionSet.managedPolicies || permissionSet.managed_policies || [];
      const inlinePolicies = permissionSet.inlinePolicies || permissionSet.inline_policies || {};
      const cmpIds = (permissionSet.customerManagedPolicies || permissionSet.customer_managed_policies || []).map((c) => c.id);

      setEditForm({
        name: permissionSet.name || '',
        description: permissionSet.description || '',
        sessionDuration: permissionSet.sessionDuration || permissionSet.session_duration || 'PT8H',
        relayState: permissionSet.relayState || permissionSet.relay_state || '',
        managedPolicies: [...managedPolicies],
        inlinePolicies: { ...inlinePolicies },
        customerManagedPolicyIds: cmpIds,
        realm: permissionSet.realm || 'master'
      });
    }
  }, [permissionSet]);

  useEffect(() => {
    let mounted = true;
    apiService.listCustomerManagedPolicies({ pageSize: 200 })
      .then((res) => {
        if (mounted) setCmpLibrary(res?.policies || []);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const loadPolicies = async () => {
      if (!policiesFetched.current && (!awsManagedPolicies || awsManagedPolicies.length === 0)) {
        try {
          await fetchAwsPolicies();
        } catch (error) {
          console.error('Failed to fetch AWS policies:', error);
        } finally {
          policiesFetched.current = true;
        }
      }
    };

    loadPolicies();
  }, [fetchAwsPolicies, awsManagedPolicies?.length]);

  useEffect(() => {
    if (isEditing && (!awsManagedPolicies || awsManagedPolicies.length === 0)) {
      fetchAwsPolicies();
    }
  }, [isEditing, awsManagedPolicies, fetchAwsPolicies]);

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    const formattedData = {
      name: editForm.name,
      description: editForm.description,
      session_duration: editForm.sessionDuration,
      relay_state: (editForm.relayState || '').trim(),
      managed_policies: editForm.managedPolicies,
      inline_policies: editForm.inlinePolicies,
      customer_managed_policy_ids: editForm.customerManagedPolicyIds,
      realm: editForm.realm || 'master'
    };

    setIsSaving(true);
    try {
      const success = await onUpdate(permissionSet.id, formattedData);
      if (success) {
        setIsEditing(false);
        // Reset inline policy edit state
        setEditingPolicyName(null);
        setInlinePolicyName('');
        setInlinePolicyContent('');
        setPolicyValidation({ isValidating: false, result: null, error: null });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = () => {
    setDeleteDialog({ open: true, isDeleting: false, error: null });
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, isDeleting: false, error: null });
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    setDeleteDialog(prev => ({ ...prev, isDeleting: true, error: null }));
    try {
      const result = await onDelete(permissionSet.id, permissionSet.name);
      if (result) {
        onClose();
        setDeleteDialog({ open: false, isDeleting: false, error: null });
      } else {
        // Delete failed - the error will be shown via global toast, but we also keep track
        setDeleteDialog(prev => ({ ...prev, isDeleting: false }));
      }
    } catch (error) {
      setDeleteDialog(prev => ({ ...prev, isDeleting: false, error: error.message }));
    }
  };

  const handleAddManagedPolicy = (policyArn) => {
    if (editForm.managedPolicies.includes(policyArn)) return;

    setEditForm({
      ...editForm,
      managedPolicies: [...editForm.managedPolicies, policyArn]
    });
  };

  const handleRemoveManagedPolicy = (policyArn) => {
    setEditForm({
      ...editForm,
      managedPolicies: editForm.managedPolicies.filter(arn => arn !== policyArn)
    });
  };

  const debouncedValidatePolicy = useCallback(
    debounce((policyContent) => {
      if (!policyContent || policyContent.trim() === '') {
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: null
        });
        return;
      }

      validatePolicy(policyContent);
    }, 800),
    []
  );

  useEffect(() => {
    if (inlinePolicyContent) {
      debouncedValidatePolicy(inlinePolicyContent);
    }

    return () => {
      debouncedValidatePolicy.cancel();
    };
  }, [inlinePolicyContent, debouncedValidatePolicy]);

  const validatePolicy = async (policyContent) => {
    if (!policyContent || policyContent.trim() === '') {
      return;
    }

    try {
      JSON.parse(policyContent);
    } catch (error) {
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: "Invalid JSON format. Please check the syntax."
      });
      return;
    }

    setPolicyValidation({
      isValidating: true,
      result: null,
      error: null
    });

    try {
      const response = await apiService.validateIAMPolicy(policyContent);
      if (response.success) {
        setPolicyValidation({
          isValidating: false,
          result: response.data,
          error: null
        });
      } else {
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: response.message || "Failed to validate policy"
        });
      }
    } catch (error) {
      console.error("Policy validation error:", error);
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: error.message || "An error occurred during policy validation"
      });
    }
  };

  const renderValidationResults = () => {
    if (policyValidation.isValidating) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#F39C12' }}>
          <CircularProgress size={14} sx={{ color: '#F39C12' }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Validating policy...
          </Typography>
        </Box>
      );
    }

    if (policyValidation.error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#E74C3C' }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            {policyValidation.error}
          </Typography>
        </Box>
      );
    }

    if (!policyValidation.result) {
      return null;
    }

    const findings = policyValidation.result.findings || [];

    if (findings.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#27AE60' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy is valid
          </Typography>
        </Box>
      );
    }

    const errors = findings.filter(f => f.findingType === 'ERROR');
    const warnings = findings.filter(f => f.findingType === 'WARNING');
    const suggestions = findings.filter(f => f.findingType === 'SUGGESTION');

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#F39C12' }}>
          <WarningIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy validation found issues
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FDF2F2', borderRadius: '6px', border: '1px solid #E74C3C20' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#E74C3C', mb: 0.5 }}>
              Errors ({errors.length})
            </Typography>
            {errors.map((error, index) => (
              <Typography key={`error-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{error.issueCode || 'Error'}:</strong> {error.findingDetails || 'Invalid policy'}
              </Typography>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FEF9E7', borderRadius: '6px', border: '1px solid #F39C1220' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#F39C12', mb: 0.5 }}>
              Warnings ({warnings.length})
            </Typography>
            {warnings.map((warning, index) => (
              <Typography key={`warning-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{warning.issueCode || 'Warning'}:</strong> {warning.findingDetails || 'Policy warning'}
              </Typography>
            ))}
          </Box>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ p: 1.5, backgroundColor: '#EBF5FB', borderRadius: '6px', border: '1px solid #4698D320' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#4698D3', mb: 0.5 }}>
              Suggestions ({suggestions.length})
            </Typography>
            {suggestions.map((suggestion, index) => (
              <Typography key={`suggestion-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{suggestion.issueCode || 'Suggestion'}:</strong> {suggestion.findingDetails || 'Policy suggestion'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const handleAddInlinePolicy = async () => {
    if (!inlinePolicyName || !inlinePolicyContent.trim()) return;

    try {
      JSON.parse(inlinePolicyContent);

      if (policyValidation.error || (policyValidation.result && policyValidation.result.findings &&
        policyValidation.result.findings.some(f => f.findingType === 'ERROR'))) {
        const action = editingPolicyName ? 'update' : 'add';
        const confirmed = await confirm({
          title: 'Policy Validation Errors',
          message: `The policy has validation errors. Are you sure you want to ${action} it anyway?`,
          confirmText: action === 'update' ? 'Update Anyway' : 'Add Anyway',
          variant: 'warning',
        });
        if (!confirmed) {
          return;
        }
      }

      // Check if we're updating an existing policy
      if (editingPolicyName) {
        const newInlinePolicies = { ...editForm.inlinePolicies };

        // If the policy name changed, remove the old one
        if (editingPolicyName !== inlinePolicyName) {
          delete newInlinePolicies[editingPolicyName];
        }

        // Add/update with the new name and content
        newInlinePolicies[inlinePolicyName] = inlinePolicyContent;

        setEditForm({
          ...editForm,
          inlinePolicies: newInlinePolicies
        });
      } else {
        // Adding a new policy
        setEditForm({
          ...editForm,
          inlinePolicies: {
            ...editForm.inlinePolicies,
            [inlinePolicyName]: inlinePolicyContent
          }
        });
      }

      // Reset form state
      setEditingPolicyName(null);
      setInlinePolicyName('');
      setInlinePolicyContent('');
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: null
      });
    } catch (error) {
      alert('Invalid JSON format for inline policy');
    }
  };

  const handleRemoveInlinePolicy = (policyName) => {
    const newInlinePolicies = { ...editForm.inlinePolicies };
    delete newInlinePolicies[policyName];

    setEditForm({
      ...editForm,
      inlinePolicies: newInlinePolicies
    });

    // If we're editing this policy, clear the edit state
    if (editingPolicyName === policyName) {
      handleCancelEditInlinePolicy();
    }
  };

  const handleEditInlinePolicy = (policyName) => {
    const policyContent = editForm.inlinePolicies[policyName];
    setEditingPolicyName(policyName);
    setInlinePolicyName(policyName);
    setInlinePolicyContent(policyContent);
    // Trigger validation for the loaded content
    debouncedValidatePolicy(policyContent);
  };

  const handleCancelEditInlinePolicy = () => {
    setEditingPolicyName(null);
    setInlinePolicyName('');
    setInlinePolicyContent('');
    setPolicyValidation({
      isValidating: false,
      result: null,
      error: null
    });
  };

  const getPolicyCategories = () => {
    const categories = new Set();
    (awsManagedPolicies || []).forEach(policy => {
      if (policy.name.includes('Amazon')) categories.add('Amazon Services');
      else if (policy.name.includes('AWS')) categories.add('AWS Services');
      else if (policy.name.includes('IAM') || policy.name.includes('Security')) categories.add('Security & IAM');
      else if (policy.name.includes('ReadOnly') || policy.name.includes('Read')) categories.add('Read Only');
      else if (policy.name.includes('Admin') || policy.name.includes('Full')) categories.add('Administrative');
      else if (policy.name.includes('Power') || policy.name.includes('Developer')) categories.add('Developer');
      else categories.add('Other');
    });
    return Array.from(categories).sort();
  };

  const getFilteredAndSortedPolicies = () => {
    let filtered = (awsManagedPolicies || []).filter(policy => {
      const matchesSearch = !policySearchTerm ||
        policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
        (policy.description && policy.description.toLowerCase().includes(policySearchTerm.toLowerCase())) ||
        policy.arn.toLowerCase().includes(policySearchTerm.toLowerCase());

      const matchesCategory = !selectedPolicyCategory ||
        (selectedPolicyCategory === 'Amazon Services' && policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'AWS Services' && policy.name.includes('AWS') && !policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'Security & IAM' && (policy.name.includes('IAM') || policy.name.includes('Security'))) ||
        (selectedPolicyCategory === 'Read Only' && (policy.name.includes('ReadOnly') || policy.name.includes('Read'))) ||
        (selectedPolicyCategory === 'Administrative' && (policy.name.includes('Admin') || policy.name.includes('Full'))) ||
        (selectedPolicyCategory === 'Developer' && (policy.name.includes('Power') || policy.name.includes('Developer'))) ||
        (selectedPolicyCategory === 'Other' && !policy.name.includes('Amazon') && !policy.name.includes('AWS') &&
          !policy.name.includes('IAM') && !policy.name.includes('Security') && !policy.name.includes('ReadOnly') &&
          !policy.name.includes('Read') && !policy.name.includes('Admin') && !policy.name.includes('Full') &&
          !policy.name.includes('Power') && !policy.name.includes('Developer'));

      return matchesSearch && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (policySortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'service':
          return (a.name.split(/[A-Z]/)[0] || '').localeCompare(b.name.split(/[A-Z]/)[0] || '');
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  };

  const filteredPolicies = getFilteredAndSortedPolicies();
  const totalPages = Math.ceil(filteredPolicies.length / policiesPerPage);
  const paginatedPolicies = filteredPolicies.slice(
    (currentPage - 1) * policiesPerPage,
    currentPage * policiesPerPage
  );

  const handleShowPolicyPicker = async () => {
    if (!showPolicyPicker) {
      if (!awsManagedPolicies || awsManagedPolicies.length === 0) {
        setIsLoadingPolicies(true);
        try {
          await fetchAwsPolicies();
        } catch (error) {
          console.error('Failed to fetch AWS policies:', error);
        } finally {
          setIsLoadingPolicies(false);
        }
      }
    }
    setShowPolicyPicker(!showPolicyPicker);
  };

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#27AE60',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#27AE60',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: '#27AE60',
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="initial" animate="animate" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
      {/* Header */}
      <motion.div variants={itemVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#F0F9F4',
          borderRadius: '12px 12px 0 0',
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: '#27AE60',
              width: 48,
              height: 48,
            }}
          >
            <SecurityIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#253E66',
              }}
            >
              {permissionSet.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Permission Set Details
            </Typography>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Content */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '0 0 12px 12px',
          p: 3,
        }}
      >
        {!isEditing ? (
          // View Mode
          <>
            {/* Action Buttons */}
            <motion.div variants={itemVariants}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <ReadOnlyButton
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                sx={{
                  backgroundColor: '#27AE60',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#219A52',
                  },
                }}
              >
                Edit
              </ReadOnlyButton>
              <ReadOnlyButton
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#E74C3C',
                  color: '#E74C3C',
                  '&:hover': {
                    backgroundColor: '#FDF2F2',
                    borderColor: '#E74C3C',
                  },
                }}
              >
                Delete
              </ReadOnlyButton>
            </Box>
            </motion.div>

            {/* Basic Information */}
            <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#253E66',
                  mb: 2,
                }}
              >
                Basic Information
              </Typography>
              <Box sx={{ backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', fontWeight: 500, mb: 0.5 }}>
                      Description
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66' }}>
                      {permissionSet.description || 'No description'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', fontWeight: 500, mb: 0.5 }}>
                      Session Duration
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66' }}>
                      {formatDuration(permissionSet.sessionDuration || permissionSet.session_duration)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', fontWeight: 500, mb: 0.5 }}>
                      Relay state
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66', wordBreak: 'break-all' }}>
                      {(permissionSet.relayState || permissionSet.relay_state) || 'Not set'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            </motion.div>

            {/* AWS Managed Policies */}
            <motion.div variants={itemVariants} style={{ marginBottom: 24 }}>
            <Accordion
              defaultExpanded
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px !important',
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PolicyIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                    AWS Managed Policies ({(permissionSet.managedPolicies || permissionSet.managed_policies || []).length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {(permissionSet.managedPolicies || permissionSet.managed_policies) && (permissionSet.managedPolicies || permissionSet.managed_policies).length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {(permissionSet.managedPolicies || permissionSet.managed_policies).map((policyArn, index) => {
                      const policyDetails = (awsManagedPolicies || []).find(p => p.arn === policyArn);
                      return (
                        <ListItem
                          key={policyArn}
                          sx={{
                            borderBottom: index < (permissionSet.managedPolicies || permissionSet.managed_policies).length - 1 ? '1px solid #E5E5E5' : 'none',
                            py: 1.5,
                            px: 0
                          }}
                        >
                          <ListItemText
                            primary={policyDetails?.name || 'Unknown Policy'}
                            secondary={
                              <Box>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#999999' }}>
                                  {policyArn}
                                </Typography>
                                {policyDetails?.description && (
                                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666666', fontWeight: 500, mt: 0.5 }}>
                                    {policyDetails.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{
                              sx: { fontFamily: 'Metropolis, sans-serif', color: '#253E66', fontWeight: 600, fontSize: '0.9rem' }
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                    No managed policies attached
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            </motion.div>

            {/* Inline Policies */}
            <motion.div variants={itemVariants}>
            <Accordion
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px !important',
                mb: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CodeIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                    Inline Policies ({countInlinePolicies(permissionSet.inlinePolicies || permissionSet.inline_policies)})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {(permissionSet.inlinePolicies || permissionSet.inline_policies) && countInlinePolicies(permissionSet.inlinePolicies || permissionSet.inline_policies) > 0 ? (
                  <Box>
                    {Object.entries(filterNonEmptyInlinePolicies(permissionSet.inlinePolicies || permissionSet.inline_policies)).map(([name, content]) => (
                      <Box key={name} sx={{ mb: 2 }}>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66', fontWeight: 600, mb: 1 }}>
                          {name}
                        </Typography>
                        <Box sx={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '6px',
                          p: 2,
                          border: '1px solid #E5E5E5',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          <Typography
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: '#666666',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              margin: 0
                            }}
                          >
                            {content}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                    No inline policies defined
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            </motion.div>

            {/* Customer Managed Policies (View) */}
            <motion.div variants={itemVariants}>
            <Accordion
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px !important',
                mb: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PolicyIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                    Customer Managed Policies ({(permissionSet.customerManagedPolicies || permissionSet.customer_managed_policies || []).length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {(permissionSet.customerManagedPolicies || permissionSet.customer_managed_policies || []).length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {(permissionSet.customerManagedPolicies || permissionSet.customer_managed_policies || []).map((cmp) => (
                      <ListItem
                        key={cmp.id}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '6px',
                          mb: 1,
                          border: '1px solid #E5E5E5',
                        }}
                      >
                        <ListItemText
                          primary={cmp.name}
                          secondary={cmp.description || null}
                          primaryTypographyProps={{
                            sx: { fontFamily: 'Metropolis, sans-serif', color: '#253E66', fontWeight: 600, fontSize: '0.9rem' }
                          }}
                          secondaryTypographyProps={{
                            sx: { fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.8rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                    No customer-managed policies attached
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            </motion.div>
          </>
        ) : (
          // Edit Mode
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
          <Box>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#253E66',
                mb: 3,
              }}
            >
              Edit Permission Set
            </Typography>

            <Box component="form" onSubmit={handleSubmitEdit}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name}
                disabled
                sx={{
                  ...inputStyle,
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyle['& .MuiOutlinedInput-root'],
                    backgroundColor: '#F9FBFD',
                  },
                }}
                helperText="Permission set name is immutable as it affects AWS role names"
              />

              <TextField
                fullWidth
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                multiline
                rows={3}
                sx={{ ...inputStyle, mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', '&.Mui-focused': { color: '#27AE60' } }}>
                  Session Duration
                </InputLabel>
                <Select
                  value={editForm.sessionDuration}
                  onChange={(e) => setEditForm({ ...editForm, sessionDuration: e.target.value })}
                  label="Session Duration"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E5E5',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#27AE60',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#27AE60',
                    },
                  }}
                >
                  <MenuItem value="PT1H">1 Hour</MenuItem>
                  <MenuItem value="PT2H">2 Hours</MenuItem>
                  <MenuItem value="PT4H">4 Hours</MenuItem>
                  <MenuItem value="PT8H">8 Hours</MenuItem>
                  <MenuItem value="PT12H">12 Hours</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Relay state — optional"
                value={editForm.relayState}
                onChange={(e) => setEditForm({ ...editForm, relayState: e.target.value.slice(0, 320) })}
                placeholder="https://console.aws.amazon.com/ec2/v2/home?region=us-east-1"
                helperText={`Destination URL after AWS console federation. Up to 320 characters; alphanumeric, spaces, and & $ @ : / ? # = + - _ . ~ { }. (${(editForm.relayState || '').length}/320)`}
                inputProps={{ maxLength: 320 }}
                sx={{ ...inputStyle, mb: 3 }}
              />

              {/* AWS Managed Policies Section */}
              <Accordion
                defaultExpanded
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                  sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PolicyIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      AWS Managed Policies ({editForm.managedPolicies.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {/* Selected Policies */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                      Selected Policies
                    </Typography>
                    {editForm.managedPolicies.length === 0 ? (
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                        No policies selected
                      </Typography>
                    ) : (
                      <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 1 }}>
                        {editForm.managedPolicies.map(policyArn => {
                          const policyDetails = (awsManagedPolicies || []).find(p => p.arn === policyArn) || { name: 'Unknown Policy' };
                          return (
                            <Box key={policyArn} sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              px: 1.5,
                              mb: 0.5,
                              backgroundColor: '#F0F9F4',
                              borderRadius: '4px',
                              '&:last-child': { mb: 0 }
                            }}>
                              <Box>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                                  {policyDetails.name}
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#999999' }}>
                                  {policyArn}
                                </Typography>
                              </Box>
                              <ReadOnlyIconButton
                                onClick={() => handleRemoveManagedPolicy(policyArn)}
                                size="small"
                                sx={{ color: '#E74C3C' }}
                              >
                                <RemoveIcon fontSize="small" />
                              </ReadOnlyIconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>

                  <ReadOnlyButton
                    variant="outlined"
                    onClick={handleShowPolicyPicker}
                    disabled={isLoadingPolicies}
                    startIcon={isLoadingPolicies ? <CircularProgress size={16} /> : <AddIcon />}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#E5E5E5',
                      color: '#666666',
                      mb: 2,
                      '&:hover': {
                        borderColor: '#27AE60',
                        backgroundColor: '#F0F9F4',
                        color: '#27AE60',
                      },
                    }}
                  >
                    {isLoadingPolicies ? 'Loading Policies...' : showPolicyPicker ? 'Hide Policy Browser' : 'Browse & Add Policies'}
                  </ReadOnlyButton>

                  {/* Policy Picker */}
                  {showPolicyPicker && (
                    <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2 }}>
                      {isLoadingPolicies ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={24} sx={{ color: '#27AE60', mb: 2 }} />
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                            Fetching AWS managed policies...
                          </Typography>
                        </Box>
                      ) : (!awsManagedPolicies || awsManagedPolicies.length === 0) ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                            No AWS managed policies available.
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={async () => {
                              setIsLoadingPolicies(true);
                              try {
                                await fetchAwsPolicies();
                              } catch (error) {
                                console.error('Retry fetch failed:', error);
                              } finally {
                                setIsLoadingPolicies(false);
                              }
                            }}
                            sx={{
                              mt: 2,
                              fontFamily: 'Metropolis, sans-serif',
                              textTransform: 'none',
                              borderColor: '#E5E5E5',
                            }}
                          >
                            Retry Loading Policies
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              fullWidth
                              placeholder="Search policies by name, description, or ARN..."
                              value={policySearchTerm}
                              onChange={(e) => {
                                setPolicySearchTerm(e.target.value);
                                setCurrentPage(1);
                              }}
                              size="small"
                              sx={{ ...inputStyle, mb: 2 }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Category</InputLabel>
                                <Select
                                  value={selectedPolicyCategory}
                                  onChange={(e) => {
                                    setSelectedPolicyCategory(e.target.value);
                                    setCurrentPage(1);
                                  }}
                                  label="Category"
                                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                                >
                                  <MenuItem value="">All Categories</MenuItem>
                                  {getPolicyCategories().map(category => (
                                    <MenuItem key={category} value={category}>{category}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Sort</InputLabel>
                                <Select
                                  value={policySortBy}
                                  onChange={(e) => setPolicySortBy(e.target.value)}
                                  label="Sort"
                                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                                >
                                  <MenuItem value="name">Sort: A-Z</MenuItem>
                                  <MenuItem value="nameDesc">Sort: Z-A</MenuItem>
                                  <MenuItem value="service">Sort: Service</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>

                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666666', fontWeight: 500, mb: 1.5 }}>
                            Showing {paginatedPolicies.length} of {filteredPolicies.length} policies
                            {policySearchTerm && ` matching "${policySearchTerm}"`}
                            {selectedPolicyCategory && ` in ${selectedPolicyCategory}`}
                          </Typography>

                          <Box sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #E5E5E5',
                            borderRadius: '6px',
                          }}>
                            {paginatedPolicies.map(policy => {
                              const isSelected = editForm.managedPolicies.includes(policy.arn);
                              return (
                                <Box key={policy.arn} sx={{
                                  p: 1.5,
                                  borderBottom: '1px solid #E5E5E5',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  backgroundColor: isSelected ? '#F0F9F4' : '#FFFFFF',
                                  '&:hover': {
                                    backgroundColor: isSelected ? '#E8F6EC' : '#F9FBFD',
                                  },
                                  '&:last-child': { borderBottom: 'none' },
                                }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                                      {policy.name}
                                    </Typography>
                                    {policy.description && (
                                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', fontWeight: 500 }}>
                                        {policy.description}
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#999999', mt: 0.25 }}>
                                      {policy.arn}
                                    </Typography>
                                  </Box>
                                  <ReadOnlyButton
                                    variant={isSelected ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => isSelected ? handleRemoveManagedPolicy(policy.arn) : handleAddManagedPolicy(policy.arn)}
                                    sx={{
                                      ml: 2,
                                      minWidth: '80px',
                                      fontFamily: 'Metropolis, sans-serif',
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      ...(isSelected ? {
                                        backgroundColor: '#27AE60',
                                        '&:hover': { backgroundColor: '#219A52' },
                                      } : {
                                        borderColor: '#E5E5E5',
                                        color: '#666666',
                                        '&:hover': { borderColor: '#27AE60', color: '#27AE60' },
                                      }),
                                    }}
                                  >
                                    {isSelected ? 'Selected' : '+ Add'}
                                  </ReadOnlyButton>
                                </Box>
                              );
                            })}
                          </Box>

                          {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                              <Button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                              >
                                Previous
                              </Button>
                              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                                Page {currentPage} of {totalPages}
                              </Typography>
                              <Button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                              >
                                Next
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Inline Policies Section */}
              <Accordion
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px !important',
                  mb: 3,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                  sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CodeIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      Inline Policies ({countInlinePolicies(editForm.inlinePolicies)})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {/* Add/Edit Inline Policy */}
                  <Box sx={{ backgroundColor: editingPolicyName ? '#FFF9E6' : '#FFFFFF', borderRadius: '6px', border: `1px solid ${editingPolicyName ? '#F39C12' : '#E5E5E5'}`, p: 2, mb: 2 }}>
                    {editingPolicyName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: '1px solid #F39C1240' }}>
                        <EditIcon sx={{ color: '#F39C12', fontSize: 18 }} />
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#F39C12', fontWeight: 600 }}>
                          Editing: {editingPolicyName}
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      label="Policy Name"
                      placeholder="Enter policy name"
                      value={inlinePolicyName}
                      onChange={(e) => setInlinePolicyName(filterInput(e.target.value, 'IAM_POLICY_NAME'))}
                      helperText="Only letters, numbers, and + = , . @ _ - are allowed (no spaces)"
                      inputProps={{ maxLength: 128 }}
                      size="small"
                      sx={{ ...inputStyle, mb: 2 }}
                    />

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                          Policy JSON
                        </Typography>
                        {policyValidation.isValidating && (
                          <Chip
                            label="Validating..."
                            size="small"
                            sx={{ backgroundColor: '#FEF9E7', color: '#F39C12', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                          />
                        )}
                        {!policyValidation.isValidating && policyValidation.result && !policyValidation.error &&
                          !policyValidation.result.findings?.some(f => f.findingType === 'ERROR') && (
                          <Chip
                            label="Valid"
                            size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            sx={{ backgroundColor: '#F0F9F4', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                          />
                        )}
                        <PolicyGeneratorLink sx={{ ml: 'auto' }} />
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={10}
                        placeholder="Enter policy JSON"
                        value={inlinePolicyContent}
                        onChange={(e) => setInlinePolicyContent(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderColor: policyValidation.error || (policyValidation.result?.findings?.some(f => f.findingType === 'ERROR'))
                                ? '#E74C3C'
                                : '#E5E5E5',
                            },
                            '&:hover fieldset': {
                              borderColor: '#27AE60',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#27AE60',
                            },
                          },
                        }}
                      />
                      {renderValidationResults()}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      {editingPolicyName && (
                        <Button
                          variant="outlined"
                          onClick={handleCancelEditInlinePolicy}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: '#E5E5E5',
                            color: '#666666',
                            '&:hover': {
                              borderColor: '#999999',
                              backgroundColor: '#F9FBFD',
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                      <ReadOnlyButton
                        variant="contained"
                        onClick={handleAddInlinePolicy}
                        disabled={!inlinePolicyName || !inlinePolicyContent || policyValidation.isValidating}
                        startIcon={editingPolicyName ? <EditIcon /> : <AddIcon />}
                        sx={{
                          backgroundColor: editingPolicyName ? '#F39C12' : '#27AE60',
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: editingPolicyName ? '#E67E22' : '#219A52',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#E5E5E5',
                            color: '#999999',
                          },
                        }}
                      >
                        {editingPolicyName ? 'Update Policy' : 'Add Policy'}
                      </ReadOnlyButton>
                    </Box>
                  </Box>

                  {/* Existing Inline Policies */}
                  {countInlinePolicies(editForm.inlinePolicies) > 0 && (
                    <Box>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                        Current Inline Policies ({countInlinePolicies(editForm.inlinePolicies)})
                      </Typography>
                      {Object.entries(filterNonEmptyInlinePolicies(editForm.inlinePolicies)).map(([name, content]) => {
                        const isBeingEdited = editingPolicyName === name;
                        return (
                          <Box key={name} sx={{
                            backgroundColor: isBeingEdited ? '#FFF9E6' : '#FFFFFF',
                            borderRadius: '6px',
                            border: `1px solid ${isBeingEdited ? '#F39C12' : '#E5E5E5'}`,
                            p: 2,
                            mb: 1,
                            opacity: isBeingEdited ? 0.7 : 1,
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66', fontWeight: 600 }}>
                                  {name}
                                </Typography>
                                {isBeingEdited && (
                                  <Chip
                                    label="Editing"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#F39C12',
                                      color: '#FFFFFF',
                                      fontFamily: 'Metropolis, sans-serif',
                                      fontSize: '0.65rem',
                                      height: '20px',
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <ReadOnlyIconButton
                                  onClick={() => handleEditInlinePolicy(name)}
                                  size="small"
                                  disabled={isBeingEdited}
                                  sx={{
                                    color: isBeingEdited ? '#999999' : '#4698D3',
                                    '&:hover': {
                                      backgroundColor: '#EBF5FB',
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </ReadOnlyIconButton>
                                <ReadOnlyIconButton
                                  onClick={() => handleRemoveInlinePolicy(name)}
                                  size="small"
                                  sx={{ color: '#E74C3C' }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </ReadOnlyIconButton>
                              </Box>
                            </Box>
                            <Box sx={{
                              backgroundColor: isBeingEdited ? '#FEF9E740' : '#F9FBFD',
                              borderRadius: '4px',
                              p: 1.5,
                              maxHeight: '150px',
                              overflow: 'auto',
                              border: `1px solid ${isBeingEdited ? '#F39C1240' : '#E5E5E5'}`,
                            }}>
                              <Typography
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  color: '#666666',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  margin: 0
                                }}
                              >
                                {content.length > 200 ? `${content.substring(0, 200)}...` : content}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Customer Managed Policies Section (Edit) */}
              <Accordion
                sx={{
                  mt: 1,
                  boxShadow: 'none',
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  mb: 3,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: '#F4F8FB',
                    borderRadius: '8px',
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PolicyIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      Customer Managed Policies ({editForm.customerManagedPolicyIds.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Reusable policies from the library. Edits propagate to every account using this permission set.
                  </Typography>
                  {cmpLibrary.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No customer-managed policies in the library yet. Create some from the sidebar.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {cmpLibrary.map((c) => {
                        const selected = editForm.customerManagedPolicyIds.includes(c.id);
                        return (
                          <Box
                            key={c.id}
                            onClick={() => {
                              setEditForm((prev) => ({
                                ...prev,
                                customerManagedPolicyIds: selected
                                  ? prev.customerManagedPolicyIds.filter((x) => x !== c.id)
                                  : [...prev.customerManagedPolicyIds, c.id],
                              }));
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#F9F9F9' },
                            }}
                          >
                            <Checkbox
                              checked={selected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => {
                                setEditForm((prev) => ({
                                  ...prev,
                                  customerManagedPolicyIds: selected
                                    ? prev.customerManagedPolicyIds.filter((x) => x !== c.id)
                                    : [...prev.customerManagedPolicyIds, c.id],
                                }));
                              }}
                              size="small"
                              sx={{ color: '#9B59B6', '&.Mui-checked': { color: '#9B59B6' } }}
                            />
                            <Box>
                              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>{c.name}</Typography>
                              {c.description && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Metropolis, sans-serif' }}>{c.description}</Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsEditing(false);
                    // Reset inline policy edit state
                    setEditingPolicyName(null);
                    setInlinePolicyName('');
                    setInlinePolicyContent('');
                    setPolicyValidation({ isValidating: false, result: null, error: null });
                  }}
                  disabled={isSaving}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: '#E5E5E5',
                    color: '#666666',
                    '&:hover': {
                      borderColor: '#999999',
                      backgroundColor: '#F9FBFD',
                    },
                  }}
                >
                  Cancel
                </Button>
                <ReadOnlyButton
                  type="submit"
                  variant="contained"
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
                  sx={{
                    backgroundColor: '#27AE60',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    minWidth: 140,
                    '&:hover': {
                      backgroundColor: '#219A52',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#27AE60',
                      opacity: 0.7,
                      color: '#FFFFFF',
                    },
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </ReadOnlyButton>
              </Box>
            </Box>
          </Box>
          </motion.div>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #E5E5E5',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ color: '#E74C3C', fontSize: 24 }} />
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
                fontSize: '1.1rem',
              }}
            >
              Delete Permission Set
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDeleteDialog} size="small" disabled={deleteDialog.isDeleting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#253E66',
              mb: 2,
            }}
          >
            Are you sure you want to delete the permission set{' '}
            <strong>"{permissionSet?.name}"</strong>?
          </Typography>
          <Alert
            severity="warning"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          >
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', fontWeight: 500 }}>
              This action cannot be undone.
            </Typography>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', mt: 0.5, color: '#666666' }}>
              If this permission set has active assignments, you must delete all assignments first before deleting the permission set.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E5E5', p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteDialog.isDeleting}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteDialog.isDeleting}
            startIcon={deleteDialog.isDeleting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <DeleteIcon />}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#C0392B' },
              '&.Mui-disabled': {
                backgroundColor: '#E74C3C',
                opacity: 0.7,
                color: '#FFFFFF',
              },
            }}
          >
            {deleteDialog.isDeleting ? 'Deleting...' : 'Delete Permission Set'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default PermissionSetDetails;
